import { useState, useEffect, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import DeliveryTicket from '../../components/print/DeliveryTicket';
import { BASE_URL } from "../../apiConfig";
import { useAuth } from "../../context/AuthProvider";
import { useLanguage } from "../../context/LanguageProvider";
import { useParams } from "react-router-dom";
import { 
  BriefcaseBusiness, 
  Truck, 
  Clock, 
  Package, 
  XCircle, 
  CheckCircle, 
  ChevronLeft, 
  ChevronRight,
  RefreshCcw, Search, 
  Loader2, 
  Send,
  AlertCircle,
  ChevronDown,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from 'lucide-react';
import OrderList from '../../elements/Order_list'; 
import { toast } from "sonner";
import { statuses } from "../../cors/statusMap";
import FilterOrders from "../../cors/FilterOrders";
import LivreurSelectionModal from '../../components/modals/LivreurSelectionModal';
import Select from "react-select";


const PER_PAGE_OPTIONS = [
  { value: 20, label: "20" },
  { value: 50, label: "50" },
  { value: 100, label: "100" },
  { value: 250, label: "250" },
];

export default function OrdersDashboard() {
  const { token, role } = useAuth();
  const { t, locale } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { status } = useParams();
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [sendingOrders, setSendingOrders] = useState(false);
  const [confirmingOrders, setConfirmingOrders] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState();
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [selectedLivreur, setSelectedLivreur] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedStatusFilters, setSelectedStatusFilters] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [showFailedOrders, setShowFailedOrders] = useState(false);
  const [showLivreurModal, setShowLivreurModal] = useState(false);
  const [ordersToPrint, setOrdersToPrint] = useState([]);
  const ticketRef = useRef();

  const handlePrint = useReactToPrint({
    contentRef: ticketRef,
    documentTitle: t('orders.delivery_tickets'),
    onAfterPrint: () => setOrdersToPrint([])
  });

  useEffect(() => {
    if (ordersToPrint.length > 0) {
      handlePrint();
    }
  }, [ordersToPrint]);
  const [state, setState] = useState({
    total_revenue: 0,
    total_orders: 0,
    orders_today: 0,
    orders_en_cours: 0
  });
  const [sendProgress, setSendProgress] = useState({
    isVisible: false,
    current: 0,
    total: 0,
    percentage: 0,
    status: 'idle',
    failedCount: 0,
    successCount: 0,
    notSentOrders: []
  });

  const [perPage, setPerPage] = useState(20);

  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    if (status !== "tout") {
      setSelectedStatusFilters([]);
    }
    fetchOrders();
  }, [status]);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    from: 0,
    to: 0
  });

  const handleSelectOrder = (orderId) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  function formatDateFR(dateString) {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).replace(/\//g, '/');
  }

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pagination.currentPage]);

  const fetchOrders = async (
    page = 1,
    search = searchTerm,
    range = dateRange,
    sellerId = selectedSeller?.value ?? null,
    livreurId = selectedLivreur?.value ?? null,
    filtersOverride,
    perPageOverride,
    productId = selectedProduct?.value ?? null,
    sortOverride,
    sortDirOverride,
  ) => {
    try {
      const effectivePerPage =
        perPageOverride !== undefined && perPageOverride !== null
          ? perPageOverride
          : perPage;
      const effectiveSortBy = sortOverride !== undefined ? sortOverride : sortBy;
      const effectiveSortDir = sortDirOverride !== undefined ? sortDirOverride : sortDir;
      const requestBody = { 
        status: status, 
        userRole: role, 
        search: search,
        sort_by: effectiveSortBy,
        sort_dir: effectiveSortDir,
      };

      console.log('Range received in fetchOrders:', range);

      if (range && range.start_date && range.end_date) {
        requestBody.range = {
          start_date: range.start_date,
          end_date: range.end_date
        };
      }

      if (sellerId) {
        requestBody.seller_id = sellerId;
      }

      if (livreurId) {
        requestBody.livreur_id = livreurId;
      }

      if (productId) {
        requestBody.product_id = productId;
      }

      const statusFiltersForRequest =
        status === "tout"
          ? (filtersOverride !== undefined ? filtersOverride : selectedStatusFilters)
          : [];
      if (Array.isArray(statusFiltersForRequest) && statusFiltersForRequest.length > 0) {
        requestBody.status_filters = statusFiltersForRequest;
      }

      console.log('Final request body:', requestBody);

      const response = await fetch(
        `${BASE_URL}/api/getorders?page=${page}&per_page=${effectivePerPage}`,
        {
          method: 'POST',
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody)
        }
      );
      const data = await response.json();
      
      if (data.code === 200) {
        setOrders(data.orders);
        setState(data.stats);
        setPerPage(data.pagination.per_page);
        setPagination({
          currentPage: data.pagination.current_page,
          perPage: data.pagination.per_page,
          total: data.pagination.total,
          lastPage: data.pagination.last_page,
          from: data.pagination.from,
          to: data.pagination.to
        });
      }
      setLoading(false);        
    } catch (error) {
      console.error("Error fetching orders:", error);
      setLoading(false);
    }
  };

  const handlePerPageChange = (opt) => {
    const next = opt?.value ?? 20;
    setPerPage(next);
    setLoading(true);
    fetchOrders(
      1,
      searchTerm,
      dateRange,
      selectedSeller?.value,
      selectedLivreur?.value,
      selectedStatusFilters,
      next
    );
  };

  const handleSort = (field) => {
    let nextDir = 'asc';
    if (sortBy === field) {
      nextDir = sortDir === 'asc' ? 'desc' : 'asc';
    }
    setSortBy(field);
    setSortDir(nextDir);
    setLoading(true);
    fetchOrders(
      1,
      searchTerm,
      dateRange,
      selectedSeller?.value,
      selectedLivreur?.value,
      selectedStatusFilters,
      perPage,
      selectedProduct?.value,
      field,
      nextDir
    );
  };

  const SortHeader = ({ field, children, className = "", center = false }) => {
    const active = sortBy === field;
    const isAsc = active && sortDir === "asc";
    return (
      <th
        onClick={() => handleSort(field)}
        className={`${className} cursor-pointer select-none hover:text-gray-800 transition-colors`}
      >
        <div className={`flex items-center gap-1 ${center ? "justify-center" : ""}`}>
          <span>{children}</span>
          {active ? (
            isAsc ? (
              <ArrowUp className="w-3 h-3 text-[var(--color-primary-500)]" />
            ) : (
              <ArrowDown className="w-3 h-3 text-[var(--color-primary-500)]" />
            )
          ) : (
            <ArrowUpDown className="w-3 h-3 text-gray-300" />
          )}
        </div>
      </th>
    );
  };

  const handleAssignToLivreur = async (livreurId) => {
    if (selectedOrders.length < 1) {
      toast.warning(t('orders.no_order_selected'));
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/api/assign-orders-to-livreur`, {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          order_ids: selectedOrders, 
          livreur_id: livreurId 
        }),
      });

      const data = await response.json();

      if (data.code === 200) {
        toast.success(`${data.assigned_count} ${t('orders.assigned_success')}`);
        
        // Filter the full order objects to print
        const selectedFullOrders = orders.filter(o => selectedOrders.includes(o.id));
        setOrdersToPrint(selectedFullOrders);

        fetchOrders(pagination.currentPage);
        setSelectedOrders([]);
        setShowLivreurModal(false);
      } else {
        toast.error(data.message || t('orders.assign_error'));
      }
    } catch (error) {
      console.error("Error assigning orders to livreur:", error);
      toast.error(t('orders.network_error'));
    }
  };

  const openConfirmModal = () => {
    if (selectedOrders.length < 1) {
      toast.warning(t('orders.no_order_selected'));
      return;
    }
    setShowConfirmModal(true);
  };

  const confirmSelectedOrders = async () => {
    if (selectedOrders.length < 1) {
      toast.warning(t('orders.no_order_selected'));
      return;
    }

    setConfirmingOrders(true);

    try {
      const response = await fetch(`${BASE_URL}/api/confirm-orders`, {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ order_ids: selectedOrders }),
      });

      const data = await response.json();

      if (data.code === 200) {
        toast.success(`${data.confirmed_count} ${t('orders.confirm_success')}`);
        setShowConfirmModal(false);
        fetchOrders(pagination.currentPage);
        setSelectedOrders([]);
      } else {
        toast.error(data.message || t('orders.confirm_error'));
      }
    } catch (error) {
      console.error("Error confirming orders:", error);
      toast.error(t('orders.confirm_error'));
    } finally {
      setConfirmingOrders(false);
    }
  };

  const handleSendOrders = async () => {
    if (selectedOrders.length < 1) {
      toast.warning(t('orders.no_order_selected'));
      return;
    }

    setSendProgress({
      isVisible: true,
      current: 0,
      total: selectedOrders.length,
      percentage: 0,
      status: 'preparing',
      failedCount: 0,
      successCount: 0,
      notSentOrders: []
    });

    setSendingOrders(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 800));

      setSendProgress(prev => ({
        ...prev,
        status: 'sending',
        current: 0,
        percentage: 5
      }));           

      const response = await fetch(`${BASE_URL}/api/send-delivrey-orders`, {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ orders: selectedOrders, company: selectedCompany }),
      });

      const data = await response.json();

      if (data.code === 200) {
        const notSentOrders = data.not_sent_orders || [];
        const successCount = selectedOrders.length - notSentOrders.length;

        setSendProgress(prev => ({
          ...prev,
          current: selectedOrders.length,
          percentage: 100,
          status: 'completed',
          successCount: successCount,
          failedCount: notSentOrders.length,
          notSentOrders: notSentOrders
        }));


        if (notSentOrders.length === 0) {
          toast.success(`${t('orders.all_sent_success')} (${selectedOrders.length} ${t('orders.orders_lowercase')})`);
        } else {
          toast.warning(`${successCount} ${t('orders.sent_success_count')}, ${notSentOrders.length} ${t('orders.failed_count')}`);
        }

        fetchOrders(pagination.currentPage);
        setSelectedOrders([]);
      } else {
        setSendProgress(prev => ({
          ...prev,
          status: 'error'
        }));
        toast.error(data.message || t('orders.send_error'));
      }
    } catch (error) {
      console.error("Error sending orders:", error);
      setSendProgress(prev => ({ ...prev, status: 'error' }));
      toast.error(t('orders.send_network_error'));
    } finally {
      setTimeout(() => {
        setSendingOrders(false);
      }, 1200);
    }
  };

  const getStatusColor = (status) => statuses[status]?.color || "bg-gray-100 text-gray-800 border-gray-200";
  const getStatusText = (status) => statuses[status]?.text || status;
  const getStatusIcon = (status, iconSize = 14) => {
    const Icon = statuses[status]?.icon;
    return Icon ? <Icon size={iconSize} className={`${locale === "ar" ? "ml-1" : "mr-1"}`} /> : null;
  };

  const paginate = (pageNumber) => fetchOrders(pageNumber, searchTerm, dateRange, selectedSeller?.value, selectedLivreur?.value);
  const nextPage = () => paginate(pagination.currentPage + 1);
  const prevPage = () => paginate(pagination.currentPage - 1);

  const getProgressStatusText = () => {
    switch (sendProgress.status) {
      case 'preparing':
        return t('orders.preparing_orders');
      case 'sending':
        return t('orders.sending_orders');
      case 'completed':
        return t('orders.sending_completed');
      case 'error':
        return t('orders.error_sending');
      default:
        return t('orders.processing');
    }
  };

  function formatCount(amount) {
    return new Intl.NumberFormat('en-US', {
    }).format(amount || 0);
  }

  function formatMoney(amount) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  }

  const OrdersTableSkeleton = () => (
    <div className="bg-white rounded-xl mb-5 shadow-sm border border-gray-100 overflow-hidden animate-pulse">
      <div className="px-6 py-2 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="h-4 w-32 bg-gray-200 rounded"></div>
          <div className="h-3 w-56 bg-gray-100 rounded"></div>
        </div>
        <div className="h-8 w-20 bg-gray-200 rounded-lg"></div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {["confirme", "nouveau"].includes(status) && [1, 2].includes(role) && (
                <th className="pt-2">
                  <div className="h-4 w-4 bg-gray-200 rounded"></div>
                </th>
              )}
              <th className={`${locale === "ar" ? "pr-6 pl-2 text-right" : "pl-6 pr-2 text-left"} py-3`}>
                <div className="h-3 w-16 bg-gray-200 rounded"></div>
              </th>
              {role !== 4 && (
                <th className={`${locale === "ar" ? "text-right" : "text-left"} px-2 py-3`}>
                  <div className="h-3 w-16 bg-gray-200 rounded"></div>
                </th>
              )}
              <th className={`${locale === "ar" ? "text-right" : "text-left"} px-2 py-3`}>
                <div className="h-3 w-16 bg-gray-200 rounded"></div>
              </th>
              {role !== 4 && (
                <th className={`${locale === "ar" ? "text-right" : "text-left"} py-3`}>
                  <div className="h-3 w-16 bg-gray-200 rounded"></div>
                </th>
              )}
              <th className="px-2 py-3">
                <div className="h-3 w-16 bg-gray-200 rounded mx-auto"></div>
              </th>
              <th className="px-2 py-3">
                <div className="h-3 w-16 bg-gray-200 rounded mx-auto"></div>
              </th>
              {role !== 4 && (
                <th className="px-2 py-3">
                  <div className="h-3 w-16 bg-gray-200 rounded mx-auto"></div>
                </th>
              )}
              <th className="py-3 pl-5">
                <div className="h-3 w-16 bg-gray-200 rounded"></div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {[...Array(5)].map((_, i) => (
              <tr key={i} className="hover:bg-gray-50">
                {["confirme", "nouveau"].includes(status) && [1, 2].includes(role) && (
                  <td className="py-3 pl-2">
                    <div className="h-4 w-4 bg-gray-200 rounded"></div>
                  </td>
                )}
                <td className="py-3 pl-6 pr-2 whitespace-nowrap">
                  <div className="space-y-2">
                    <div className="h-3 w-24 bg-gray-200 rounded"></div>
                    <div className="h-2.5 w-32 bg-gray-100 rounded"></div>
                  </div>
                </td>
                {role !== 4 && (
                  <td className="py-3 px-2">
                    <div className="space-y-2">
                      <div className="h-3 w-20 bg-gray-200 rounded"></div>
                      <div className="h-2.5 w-14 bg-gray-100 rounded"></div>
                    </div>
                  </td>
                )}
                <td className="py-3 px-2">
                  <div className="space-y-2">
                    <div className="h-3 w-28 bg-gray-200 rounded"></div>
                    <div className="h-2.5 w-24 bg-gray-100 rounded"></div>
                    <div className="h-2 w-32 bg-gray-100 rounded"></div>
                  </div>
                </td>
                {role !== 4 && (
                  <td className="py-3 px-2 min-w-[180px]">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 bg-gray-200 rounded-md"></div>
                      <div className="flex-1 space-y-1.5">
                        <div className="h-2.5 w-20 bg-gray-200 rounded"></div>
                        <div className="h-2 w-14 bg-gray-100 rounded"></div>
                      </div>
                    </div>
                  </td>
                )}
                <td className="py-3 px-2">
                  <div className="flex justify-center">
                    <div className="h-5 w-20 bg-gray-200 rounded-full"></div>
                  </div>
                </td>
                <td className="py-3 px-2">
                  <div className="space-y-1.5">
                    <div className="h-4 w-20 bg-gray-200 rounded ml-auto"></div>
                    <div className="h-2.5 w-14 bg-gray-100 rounded ml-auto"></div>
                  </div>
                </td>
                {role !== 4 && (
                  <td className="py-3 px-2">
                    <div className="flex justify-center">
                      <div className="space-y-1.5">
                        <div className="h-4 w-16 bg-gray-200 rounded"></div>
                        <div className="h-2.5 w-12 bg-gray-100 rounded"></div>
                      </div>
                    </div>
                  </td>
                )}
                <td className="py-3 pr-4">
                  <div className="flex items-center justify-end gap-3 px-2">
                    <div className="h-4 w-4 bg-gray-200 rounded"></div>
                    <div className="h-4 w-4 bg-gray-200 rounded"></div>
                    <div className="h-4 w-4 bg-gray-200 rounded"></div>
                    <div className="h-4 w-4 bg-gray-200 rounded"></div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="h-3 w-48 bg-gray-200 rounded"></div>
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 bg-gray-200 rounded-md"></div>
            <div className="h-8 w-24 bg-gray-200 rounded-md"></div>
            <div className="h-8 w-8 bg-gray-200 rounded-md"></div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-gray-50">
      {/* Progress Modal */}
      {sendProgress.isVisible && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[99] flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-xl  w-full max-w-md">
            <div className="overflow-y-auto max-h-96 my-6 pl-6 pr-4 mr-2 custom-scrollbar">
              <div className="flex items-center justify-center mb-4">
                <div className={`p-3 rounded-full ${
                  sendProgress.status === 'completed' 
                    ? sendProgress.failedCount > 0 ? 'bg-yellow-100' : 'bg-green-100'
                    : sendProgress.status === 'error' ? 'bg-red-100' : 'bg-blue-100'
                }`}>
                  {sendProgress.status === 'completed' ? (
                    sendProgress.failedCount > 0 ? (
                      <AlertCircle className="w-8 h-8 text-yellow-600" />
                    ) : (
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    )
                  ) : sendProgress.status === 'error' ? (
                    <XCircle className="w-8 h-8 text-red-600" />
                  ) : (
                    <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                  )}
                </div>
              </div>
              
              <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
                {getProgressStatusText()}
              </h3>
              
              <p className="text-sm text-gray-600 text-center mb-4">
                {sendProgress.status === 'completed' 
                  ? sendProgress.failedCount > 0
                    ? `${sendProgress.successCount} ${t('orders.sent_success_count')}, ${sendProgress.failedCount} ${t('orders.failed_count')}`
                    : `${sendProgress.total} ${t('orders.all_sent_success')}`
                  : sendProgress.status === 'error'
                  ? t('orders.error_sending')
                  : `${t('orders.processing')} ${sendProgress.total} ${t('orders.orders_lowercase')}...`
                }
              </p>

              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-3 mb-4 overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all duration-500 ease-out ${
                    sendProgress.status === 'completed' 
                      ? sendProgress.failedCount > 0 ? 'bg-yellow-500' : 'bg-green-500'
                      : sendProgress.status === 'error' ? 'bg-red-500' :
                      'bg-[var(--color-primary-500)]'
                  }`}
                  style={{ width: `${sendProgress.percentage}%` }}
                />
              </div>

              {/* Progress Text */}
              <div className="flex justify-between text-xs text-gray-500 mb-2">
                <span>{t('orders.progress')}</span>
                <span>{sendProgress.percentage}%</span>
              </div>

              {/* Count */}
              <div className="text-center text-xs text-gray-600 mb-4">
                {sendProgress.current} / {sendProgress.total} {t('orders.orders_processed')}
              </div>

              {/* Failed Orders Section */}
              {sendProgress.failedCount > 0 && sendProgress.status === 'completed' && (
                <div className="mb-4 border-t pt-4">
                  <button
                    onClick={() => setShowFailedOrders(!showFailedOrders)}
                    className="w-full flex items-center justify-between p-2 rounded-lg bg-red-50 hover:bg-red-100 transition-colors"
                  >
                    <div className="flex cursor-pointer items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-600" />
                      <span className="text-xs font-medium text-red-600">
                        {sendProgress.failedCount} {t('orders.orders_not_sent')}
                      </span>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-red-600 transition-transform ${showFailedOrders ? 'rotate-180' : ''}`} />
                  </button>

                  {showFailedOrders && (
                    <div className="mt-2 max-h-48 overflow-y-auto bg-red-50 rounded-lg p-3">
                      {sendProgress.notSentOrders.map((failedOrder, idx) => (
                        <div key={idx} className="mb-2 pb-2 border-b border-red-200 last:border-b-0">
                          <div className="flex items-start gap-2">
                            <div className="mt-0.5">
                              <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-red-900">
                                {t('orders.order_id')} {failedOrder.order_id}
                              </p>
                              <p className="text-xs text-red-700 mt-0.5 break-words">
                                {failedOrder.reason || t('orders.unknown_reason')}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Close button for completed/error states */}
              {(sendProgress.status === 'completed' || sendProgress.status === 'error') && (
                <button
                  onClick={() => setSendProgress(prev => ({ ...prev, isVisible: false }))}
                  className="w-full cursor-pointer mt-4 bg-[var(--color-primary-500)] text-xs text-white py-2 rounded-lg hover:bg-[var(--color-primary-400)] transition-colors"
                >
                  {t('orders.close')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Livreur Selection Modal */}
      <LivreurSelectionModal
        isOpen={showLivreurModal}
        onClose={() => setShowLivreurModal(false)}
        selectedOrders={selectedOrders}
        onAssign={handleAssignToLivreur}
      />

      {/* Confirm Orders Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[99]">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm mx-auto">
            <div className="flex justify-between items-center pb-3 border-b border-gray-200 mb-4">
              <h3 className="text-sm font-semibold text-gray-800">{t('orders.confirm_modal_title')}</h3>
              <button
                onClick={() => setShowConfirmModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200 cursor-pointer"
                aria-label={t('orders.close')}
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>

            <div className="flex flex-col items-center mb-6">
              <div className="p-3 rounded-full bg-green-100 mb-3">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-gray-600 text-xs text-center">
                {t('orders.confirm_modal_message', { count: selectedOrders.length })}
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-5 py-2 text-xs cursor-pointer rounded-lg text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
              >
                {t('buttons.cancel')}
              </button>
              <button
                onClick={confirmSelectedOrders}
                disabled={confirmingOrders}
                className="px-5 py-2 text-xs cursor-pointer rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {confirmingOrders && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {t('buttons.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden Print Component */}
      <div style={{ display: 'none' }}>
        <DeliveryTicket ref={ticketRef} orders={ordersToPrint} />
      </div>

      <div className="">
        <div className="max-w-7xl mx-auto px-4 pt-12 ">
          <div className="flex items-center justify-between max-sm:grid max-sm:justify-normal">
            <div className="">
              {
                status === "tout" ?
                <h1 className="text-4xl max-md:text-2xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-4">
                  {t('orders.all_orders')}
                </h1>
                :
                <h1 className="text-4xl max-md:text-2xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-4">
                  {t('orders.orders')} {getStatusText(status)}
                </h1>
              }
              <p className="text-sm max-xl:text-xs text-gray-600 max-w-2xl">
                {t('orders.manage_track_orders')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4">
        <div className="bg-white rounded-2xl border border-slate-100 dark:border-stone-900 shadow-sm py-3 px-4 my-3 max-lg:my-6">
            <FilterOrders
              role={role}
              token={token}
              status={status}
              loading={loading}
              setLoading={setLoading}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              fetchOrders={fetchOrders}
              setDateRange={setDateRange}
              selectedOrders={selectedOrders}
              handleSendOrders={handleSendOrders}
              sendingOrders={sendingOrders}
              handleConfirmOrders={openConfirmModal}
              confirmingOrders={confirmingOrders}
              selectedSeller={selectedSeller}
              setSelectedSeller={setSelectedSeller}
              selectedLivreur={selectedLivreur}
              setSelectedLivreur={setSelectedLivreur}
              selectedProduct={selectedProduct}
              setSelectedProduct={setSelectedProduct}
              selectedStatusFilters={selectedStatusFilters}
              setSelectedStatusFilters={setSelectedStatusFilters}
              selectedCompany={selectedCompany}
              setSelectedCompany={setSelectedCompany}
              setShowLivreurModal={setShowLivreurModal}
              pagination={pagination}
            />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-4">
          {/* Revenue */}
          <div className="bg-white rounded-2xl border border-slate-200 dark:border-stone-800 dark:bg-stone-800 shadow-sm p-4 transition-all hover:shadow-md">
            <div className="flex justify-between items-end w-full">
              <div className="w-3/4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 min-w-12 rounded-2xl bg-emerald-50 dark:bg-emerald-100 flex items-center justify-center">
                    <BriefcaseBusiness className="w-6 h-6 text-emerald-600" />
                  </div>

                  <div>
                    <p className="text-sm text-slate-500 dark:text-white/80">
                      {t("stats.revenue")}
                    </p>

                    <h2 className="text-xl font-bold text-slate-900 mt-1 dark:text-white">
                      {formatMoney(state.total_revenue) || 0}
                      <span className="text-lg font-semibold ml-1">MAD</span>
                    </h2>
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-3">
                  <span className="text-xs font-semibold text-emerald-600">
                    +18.2%
                  </span>

                  <span className="text-xs text-slate-400">
                    vs last month
                  </span>
                </div>
              </div>

              <div className="w-1/4 flex justify-end items-end">
                <svg
                  width="95"
                  height="42"
                  viewBox="0 0 95 42"
                  fill="none"
                >
                  <defs>
                    <linearGradient id="area" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10B981" stopOpacity="0.35" />
                      <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M2 36L14 32L24 34L36 20L46 26L58 14L70 22L82 8L93 12V42H2Z"
                    fill="url(#area)"
                  />

                  <path
                    d="M2 36L14 32L24 34L36 20L46 26L58 14L70 22L82 8L93 12"
                    stroke="#10B981"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Orders */}
          <div className="w-full bg-white rounded-2xl border border-slate-200 dark:border-stone-800 dark:bg-stone-800 shadow-sm p-4 transition-all hover:shadow-md">
            <div className="flex justify-between w-full items-end">
              <div className="w-3/4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-100 flex items-center justify-center">
                    <Package className="w-6 h-6 text-blue-600" />
                  </div>

                  <div>
                    <p className="text-sm text-slate-500 dark:text-white/80">
                      {t("stats.total_orders")}
                    </p>

                    <h2 className="text-xl font-bold text-slate-900 mt-1 dark:text-white">
                      {formatCount(state.total_orders) || 0}
                    </h2>
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-4">
                  <span className="text-xs font-semibold text-blue-600">
                    +11.1%
                  </span>

                  <span className="text-xs text-slate-400">
                    vs last month
                  </span>
                </div>
              </div>
              <div className="w-1/4 flex justify-end items-end">
                <svg width="95" height="42" viewBox="0 0 95 42" fill="none">
                  <defs>
                    <linearGradient id="blueArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2563EB" stopOpacity="0.35" />
                      <stop offset="100%" stopColor="#2563EB" stopOpacity="0" />
                    </linearGradient>
                  </defs>

                  {/* Gradient fill */}
                  <path
                    d="M2 35L14 34L25 26L36 29L47 14L58 18L70 8L82 16L93 5V42H2Z"
                    fill="url(#blueArea)"
                  />
                  <path
                    d="M2 35L14 34L25 26L36 29L47 14L58 18L70 8L82 16L93 5"
                    stroke="#2563EB"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

            </div>
          </div>

          {/* Today */}
          <div className="bg-white rounded-2xl border border-slate-200 dark:border-stone-800 dark:bg-stone-800 shadow-sm p-4 transition-all hover:shadow-md">
            <div className="flex w-full justify-between items-end">
              <div className="w-3/4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 px-3 rounded-2xl bg-yellow-50 dark:bg-yellow-100 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-yellow-500" />
                  </div>

                  <div>
                    <p className="text-sm text-nowrap text-slate-500 dark:text-white/80">
                      {t("stats.orders_today")}
                    </p>

                    <h2 className="text-xl font-bold text-slate-900 mt-1 dark:text-white">
                      {formatCount(state.orders_today) || 0}
                    </h2>
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-3">
                  <span className="text-xs text-slate-400">
                    0% vs yesterday
                  </span>
                </div>
              </div>

              <div className="w-1/4 h-10 flex items-center">
                <div className="w-full h-[3px] bg-yellow-400 rounded-full"></div>
              </div>
            </div>
          </div>

          {/* Delivery */}
          <div className="bg-white rounded-2xl border border-slate-200 dark:border-stone-800 dark:bg-stone-800 shadow-sm p-4 transition-all hover:shadow-md">
            <div className="flex w-full justify-between items-end">
              <div className="w-3/4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-orange-50 dark:bg-orange-100 flex items-center justify-center">
                    <Truck className="w-6 h-6 text-orange-500" />
                  </div>

                  <div>
                    <p className="text-sm text-slate-500 dark:text-white/80">
                      {t("stats.in_delivery")}
                    </p>

                    <h2 className="text-xl font-bold text-slate-900 mt-1 dark:text-white">
                      {formatCount(state.orders_en_cours) || 0}
                    </h2>
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-3">
                  <span className="text-xs text-slate-400">
                    Active deliveries
                  </span>
                </div>
              </div>
              <div className="w-1/4 flex justify-end items-end">
                <svg width="95" height="42" viewBox="0 0 95 42" fill="none">
                  <defs>
                    <linearGradient id="orangeArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#F97316" stopOpacity="0.35" />
                      <stop offset="90%" stopColor="#F97316" stopOpacity="0" />
                      <stop offset="100%" stopColor="#F97316" stopOpacity="0" />
                    </linearGradient>

                    <filter id="orangeGlow" x="-50%" y="-50%" width="200%" height="200%">
                      <feGaussianBlur stdDeviation="3" result="blur" />
                      <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>

                  {/* Gradient Area */}
                  <path
                    d="M2 32L14 30L25 18L36 23L47 16L58 18L70 9L82 14L93 12V42H2Z"
                    fill="url(#orangeArea)"
                  />
                  <path
                    d="M2 32L14 30L25 18L36 23L47 16L58 18L70 9L82 14L93 12"
                    stroke="#F97316"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>

        </div>

        {/* Orders Table */}
        {loading ? (
          <OrdersTableSkeleton />
        ) : (
          <div className="bg-white rounded-xl mb-5 shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-2 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h2 className="text-sm font-semibold text-gray-900">{t('orders.orders_list')}</h2>
                <p className="text-xs text-gray-600 mt-1">
                  {t('orders.showing')} {pagination.from} {t('orders.to')} {pagination.to} {t('orders.of')} {pagination.total} {t('orders.orders_lowercase')}
                </p>
                {selectedOrders.length > 0 && (
                  <p className="text-xs text-[var(--color-primary-500)] font-medium mt-1">
                    {selectedOrders.length} {t('orders.orders_selected')}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Select
                  instanceId="orders-table-per-page"
                  options={PER_PAGE_OPTIONS}
                  value={PER_PAGE_OPTIONS.find((o) => o.value === perPage) ?? PER_PAGE_OPTIONS[0]}
                  onChange={handlePerPageChange}
                  isSearchable={false}
                  className="text-xs min-w-[4.5rem]"
                  classNamePrefix="orders-per-page"
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      cursor: "pointer",
                      border: "1px solid #ccc",
                      borderRadius: "10px",
                      minHeight: "32px",
                      minWidth: "4.5rem",
                      outline: "none",
                    }),
                    dropdownIndicator: (provided) => ({ ...provided, padding: 4 }),
                    valueContainer: (provided) => ({ ...provided, padding: "0px 10px" }),
                  }}
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr className=""> 
                    {
                      ["confirme", "nouveau"].includes(status) && [1, 2].includes(role) ?
                        <th className={`pt-2 ${locale === "ar" ? "pr-2" : "pl-2"}`}>
                          <div className="inline-flex items-center">
                            <label
                              className="relative flex cursor-pointer items-center rounded-full"
                              data-ripple-dark="true"
                            >
                              <input
                                type="checkbox"
                                checked={selectedOrders.length === orders.length && orders.length > 0}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedOrders(orders.map(order => order.id));
                                  } else {
                                    setSelectedOrders([]);
                                  }
                                }}
                                className="before:content[''] peer border-gray-300 relative h-5 w-5 cursor-pointer appearance-none rounded-md border border-blue-gray-200 transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity checked:border-teal-500 checked:bg-teal-500 checked:before:bg-teal-500 hover:before:opacity-10"
                              />
                              <div className="pointer-events-none absolute top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 text-white opacity-0 transition-opacity peer-checked:opacity-100">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-3.5 w-3.5"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                  stroke="currentColor"
                                  strokeWidth="1"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  ></path>
                                </svg>
                              </div>
                            </label>
                          </div>
                        </th>
                      :''
                    }
                      <SortHeader
                        field="date"
                        className={`${status === "confirme" ? (locale === "ar" ? "pr-2 text-right" : "pl-2 text-left") : (locale === "ar" ? "pr-6 pl-2 text-right" : "pl-6 pr-2 text-left")} py-3 text-[11px] font-medium text-gray-500 uppercase tracking-wider`}
                      >
                        {t('orders.code')}
                      </SortHeader>
                    {
                      role !== 4 && (
                        <SortHeader
                          field="affiliate"
                          className={`${locale === "ar" ? "text-right" : "text-left"} px-2 py-3 text-[11px] font-medium text-gray-500 uppercase tracking-wider`}
                        >
                          {t('orders.affiliate')}
                        </SortHeader>
                      )
                    }
                    <SortHeader
                      field="client"
                      className={`${locale === "ar" ? "text-right" : "text-left"} px-2 py-3 text-[11px] font-medium text-gray-500 uppercase tracking-wider`}
                    >
                      {t('orders.client')}
                    </SortHeader>
                    
                    <th className={`${locale === "ar" ? "text-right" : "text-left"} py-3 text-[11px] font-medium text-gray-500 uppercase tracking-wider`}>
                      {t('orders.products')}
                    </th>
                      
                    <SortHeader
                      field="status"
                      center
                      className="px-2 py-3 text-[11px] font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {t('orders.status')}
                    </SortHeader>
                    <SortHeader
                      field="total"
                      center
                      className="px-2 py-3 text-[11px] font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {t('orders.total')}
                    </SortHeader>
                    {
                      role !== 4 && (
                        <th className="px-2 py-3 text-center text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                          {t('orders.profit')}
                        </th>
                      )
                    }
                    <th className="pl-5 py-3 text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                      {t('orders.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order) => (
                    <OrderList
                      key={order.id}
                      order={order}
                      formatDateFR={formatDateFR}
                      getStatusColor={getStatusColor}
                      getStatusText={getStatusText}
                      getStatusIcon={getStatusIcon}
                      fetchOrders={fetchOrders}
                      page={pagination.currentPage}
                      status={status}
                      selected={selectedOrders.includes(order.id)}
                      onSelect={handleSelectOrder}
                      role={role}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {orders.length === 0 && (
              <div className="text-center py-12">
                <Package className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">{t('orders.no_order_found')}</h3>
                <p className="mt-1 text-sm text-gray-500">{t('orders.no_order_found_desc')}</p>
              </div>
            )}

            {/* Improved Pagination */}
            {orders.length > 0 && (
              <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-gray-700">
                    {t('orders.showing')} <span className="font-medium">{pagination.from}</span> {t('orders.to')}{" "}
                    <span className="font-medium">{pagination.to}</span> {t('orders.of')}{" "}
                    <span className="font-medium">{pagination.total}</span> {t('common.results')}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={prevPage}
                      disabled={pagination.currentPage === 1}
                      className={`px-1 py-1 cursor-pointer rounded-md border ${pagination.currentPage === 1 ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, pagination.lastPage) }, (_, i) => {
                        let pageNum;
                        if (pagination.lastPage <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.currentPage >= pagination.lastPage - 2) {
                          pageNum = pagination.lastPage - 4 + i;
                        } else {
                          pageNum = pagination.currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => paginate(pageNum)}
                            className={`px-3 py-1 cursor-pointer text-sm rounded-md ${pagination.currentPage === pageNum ? 'bg-[var(--color-primary-500)] text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      {pagination.lastPage > 5 && pagination.currentPage < pagination.lastPage - 2 && (
                        <span className="px-2 text-gray-500">...</span>
                      )}
                      
                      {pagination.lastPage > 5 && pagination.currentPage < pagination.lastPage - 2 && (
                        <button
                          onClick={() => paginate(pagination.lastPage)}
                          className={`px-3 py-1 cursor-pointer text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50`}
                        >
                          {pagination.lastPage}
                        </button>
                      )}
                    </div>
                    
                    <button
                      onClick={nextPage}
                      disabled={pagination.currentPage === pagination.lastPage}
                      className={`px-1 py-1 cursor-pointer rounded-md border ${pagination.currentPage === pagination.lastPage ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
